import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { projectService } from "projects/services";
import { IProject } from "projects/models";
import { useBoundStore } from "store";

export { projectKeys, useProjects, useProject } from "./hooks/useProjects";

export const projectInfoKeys = {
  all: ["projects"] as const,
  info: (tenantId?: string, organizationId?: string) =>
    [...projectInfoKeys.all, "tenant", tenantId ?? "unknown", "organization", organizationId ?? "unknown", "info"] as const,
  options: (tenantId?: string, organizationId?: string, search?: string) =>
    [...projectInfoKeys.all, "tenant", tenantId ?? "unknown", "organization", organizationId ?? "unknown", "options", search ?? ""] as const,
};

/**
 * Fetches projects info (summary/stats) for an organization.
 */
export function useProjectsInfo(
  organizationId?: string,
  options?: { enabled?: boolean }
) {
  const { tenant } = useBoundStore();

  return useQuery({
    queryKey: projectInfoKeys.info(tenant?.id, organizationId),
    queryFn: () => projectService.getProjectsInfo(tenant!.id, organizationId as string),
    enabled: !!tenant?.id && !!organizationId && (options?.enabled ?? true),
    staleTime: 5 * 60_000,
    ...options,
  });
}

/**
 * Fetches project select options, enabled only when search is at least 3 characters.
 */
export function useProjectOptions(
  organizationId?: string,
  search?: string,
  options?: { enabled?: boolean }
) {
  const { tenant } = useBoundStore();

  return useQuery({
    queryKey: projectInfoKeys.options(tenant?.id, organizationId, search),
    queryFn: () => projectService.getProjectOptions(tenant!.id, organizationId as string, search as string),
    enabled:
      !!tenant?.id &&
      !!organizationId &&
      !!search &&
      search.length >= 3 &&
      (options?.enabled ?? true),
    staleTime: 5 * 60_000,
    ...options,
  });
}

/**
 * Mutation to add a new project. Invalidates projects queries on success.
 */
export function useAddProjectMutation() {
  const queryClient = useQueryClient();
  const { tenant } = useBoundStore();

  return useMutation({
    mutationFn: ({
      organizationId,
      project,
    }: {
      organizationId: string;
      project: IProject;
    }) => projectService.addProject(tenant!.id, organizationId, project),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectInfoKeys.all });
    },
  });
}
